import * as React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import Avatar from 'antd/lib/avatar';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Input from 'antd/lib/input';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
    ArrowRightOutlined,
    CloseCircleOutlined,
    DisconnectOutlined,
    DownOutlined,
    EditOutlined,
    LinkOutlined,
    LoadingOutlined,
    RobotOutlined,
    TagOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import sparkleIcon from '../../../assets/sparkle.svg';
import { IssueEntity, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import {
    antGrey,
    colorWithAlpha,
    drawerSectionSeparator,
    getAvatarColorForName,
    runwayColors,
    rwColors,
} from '@/components/styles/globalStyles';
import {
    selectAppIssueById,
    selectReleasesForAppId,
    selectRequiresFixRequestsApprovals,
    selectUserById,
} from '@/selectors/selectors';
import { selectAppOrgUsers, selectSortedGroups } from '@/selectors/memoizedSelectors';
import { displayTextForUserChecklistGroup, displayUserNameLong } from '@/helpers/displayHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import {
    TriageViewType,
    TRY_TRANSITION_ISSUE,
    TRY_UNMARK_ISSUE_AS_RELATED,
    TRY_UPDATE_ISSUE,
    TRY_UPDATE_ISSUE_ASSIGNEES,
    tryFetchIssue,
    tryTransitionIssue,
    tryUnmarkIssueAsRelated,
    tryUpdateIssue,
} from '@/actions/triage';
import { useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import PermissionedActionButton, { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { SCROLL_TO_RELATED_ISSUES, SOURCE_TYPE_ICON, stateMeta, SuggestedAvatarRing } from './TriageIssueCard';
import { LABEL_STYLE } from './FixRequestModule';
import { RequestFixModal, requestFixActionLabel } from './RequestFixModal';
import MarkdownParagraphWithEllipsis from '@/components/HOCs/MarkdownParagraphWithEllipsis';
import {
    buildSelectOptions,
    DESCRIPTION_MARKDOWN_COMPONENTS,
    sortUsersBySuggestedFirst,
    useBuildsForVersion,
    usePendingIssueTransition,
    AutoAssignmentReasons,
    hasAutoAssignmentReasons,
    ASSIGNEE_AGENT_PREFIX,
    ASSIGNEE_GROUP_PREFIX,
    ASSIGNEE_USER_PREFIX,
    agentDisplayName,
    currentAssigneeKey,
    isAssigneeAgentKey,
    useAgentIssueLimit,
    useAssignIssue,
    useIssueAgents,
} from './triageHelpers';
import { DefaultOptionType } from 'antd/es/select';

const BUILDS_LOADING_KEY = 'buildsLoading';

const optionAvatar = (name: string, src?: string, suggested = false) => {
    const color = getAvatarColorForName(name);
    return (
        <SuggestedAvatarRing color={color} suggested={suggested}>
            <Avatar size={20} src={src} style={{ backgroundColor: color, fontSize: 11, border: 'none', flexShrink: 0 }}>
                {name.charAt(0).toUpperCase()}
            </Avatar>
        </SuggestedAvatarRing>
    );
};

const VALID_ISSUE_TRANSITIONS: Record<IssueState, IssueState[]> = {
    [IssueState.New]: [IssueState.Assigned, IssueState.FixRequested, IssueState.NotAPriority],
    [IssueState.Assigned]: [IssueState.New, IssueState.FixRequested, IssueState.NotAPriority],
    [IssueState.FixRequested]: [IssueState.FixApproved, IssueState.FixRejected, IssueState.NotAPriority],
    [IssueState.FixApproved]: [IssueState.FixMerged, IssueState.NotAPriority],
    [IssueState.Reopened]: [IssueState.FixRequested, IssueState.NotAPriority],
    [IssueState.FixMerged]: [],
    [IssueState.FixRejected]: [],
    [IssueState.NotAPriority]: [IssueState.New],
    [IssueState.ResolvedExternally]: [],
};

const ISSUE_STATE_ORDER = Object.values(IssueState);
// Approve / reject decisions, meaningless when the app doesn't require fix approvals.
const APPROVAL_DECISION_STATES = [IssueState.FixApproved, IssueState.FixRejected];

type EditField = 'title' | 'description';

const EDIT_FIELD_VALUE: Record<EditField, (issue: IssueEntity) => string> = {
    title: (issue) => issue.title ?? '',
    description: (issue) => (issue.isDescriptionFromSource ? '' : (issue.description ?? '')),
};

const EDIT_FIELD_LABEL: Record<EditField, string> = {
    title: 'title',
    description: 'description',
};

const FieldColumn: React.FC<{ label: React.ReactNode; children: React.ReactNode; flex?: number }> = ({
    label,
    children,
    flex = 1,
}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex, minWidth: 0 }}>
        <Text style={{ color: antGrey.grey7, fontSize: 14 }}>{label}</Text>
        {children}
    </div>
);

const RelatedIssue: React.FC<{
    issue: IssueEntity;
    onOpen: () => void;
    onUnlink: () => void;
    canManage: boolean;
}> = ({ issue, onOpen, onUnlink, canManage }) => {
    const unlinking = useIsLoading(TRY_UNMARK_ISSUE_AS_RELATED, issue.id);
    return (
        <div
            onClick={onOpen}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                padding: 16,
                border: `1px solid ${antGrey.grey5}`,
                borderRadius: 4,
                background: antGrey.grey2,
                cursor: 'pointer',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    border: `1px solid ${antGrey.grey5}`,
                    borderRadius: 4,
                    background: antGrey.grey1,
                    color: antGrey.grey6,
                    fontSize: 16,
                    lineHeight: 0,
                }}
            >
                {issue.sources?.[0]?.type && SOURCE_TYPE_ICON[issue.sources[0].type]}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                <Text ellipsis style={{ fontWeight: 600, fontSize: 14, color: antGrey.grey8 }}>
                    {issue.title}
                </Text>
                {issue.description && (
                    <Text ellipsis style={{ color: antGrey.grey8, fontSize: 12 }}>
                        {issue.description}
                    </Text>
                )}
            </div>
            <Tooltip title={canManage ? 'Mark as unrelated' : DISALLOWED_ACTION_ERROR_MESSAGE}>
                <Popconfirm
                    title="Mark as unrelated"
                    description="The related link between the two issues will be removed."
                    okText="Yes, mark as unrelated"
                    okButtonProps={{ danger: true }}
                    cancelText="No"
                    placement="left"
                    onConfirm={(e) => {
                        e?.stopPropagation();
                        onUnlink();
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                >
                    <Button
                        type="text"
                        size="small"
                        icon={<DisconnectOutlined />}
                        loading={unlinking}
                        disabled={!canManage}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        style={{ color: antGrey.grey7, flexShrink: 0 }}
                    />
                </Popconfirm>
            </Tooltip>
            <ArrowRightOutlined style={{ color: rwColors.primary, fontSize: 16, marginTop: 4, flexShrink: 0 }} />
        </div>
    );
};

interface IssueSectionProps {
    orgId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
    basePath: string;
}

export const IssueSection: React.FC<IssueSectionProps> = ({ orgId, issue, viewType, basePath }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, issue.appId));
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, issue.appId));
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const { agentsById, assignableAgents } = useIssueAgents(orgId, issue.appId);
    const reporter = useSelector((state: RootState) =>
        issue.reporter ? selectUserById(state, orgId, issue.appId, issue.reporter) : undefined,
    );

    const isAssigning = useIsLoading(TRY_UPDATE_ISSUE_ASSIGNEES, issue.id);
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const { pendingTransition, setPendingTransition } = usePendingIssueTransition(isTransitioning);
    const isUpdatingIssue = useIsLoading(TRY_UPDATE_ISSUE, issue.id);
    const issueUpdated = useUpdated(TRY_UPDATE_ISSUE, issue.id);
    const canManage = useUserCanPerformAction(
        RunwayUserAction.UAManageTriageIssues,
        undefined,
        undefined,
        undefined,
        undefined,
        issue.appId,
    );
    const requiresFixApprovals = useSelector(selectRequiresFixRequestsApprovals(issue.appId));
    const noPermissionTooltip = canManage ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE;

    const [requestFixOpen, setRequestFixOpen] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);
    const [editField, setEditField] = React.useState<EditField>('title');
    const [editValue, setEditValue] = React.useState('');
    const [editSubmitted, setEditSubmitted] = React.useState(false);
    const openEdit = (field: EditField) => {
        setEditField(field);
        setEditValue(EDIT_FIELD_VALUE[field](issue));
        setEditSubmitted(false);
        setEditOpen(true);
    };
    const submitEdit = () => {
        setEditSubmitted(true);
        const payload = editField === 'title' ? { title: editValue.trim() } : { description: editValue.trim() };
        dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, ...payload }));
    };
    const editUnchanged = editValue.trim() === EDIT_FIELD_VALUE[editField](issue);
    React.useEffect(() => {
        if (editSubmitted && issueUpdated) {
            setEditSubmitted(false);
            setEditOpen(false);
        }
    }, [editSubmitted, issueUpdated]);

    const canRequestFix = !issue.fixRequest && VALID_ISSUE_TRANSITIONS[issue.state].includes(IssueState.FixRequested);
    const canNotPriority = VALID_ISSUE_TRANSITIONS[issue.state].includes(IssueState.NotAPriority);
    const version = issue.versions?.[0];
    const reporterName = reporter ? displayUserNameLong(reporter) : (issue.reporter ?? '');

    const { builds, buildsLoading } = useBuildsForVersion(orgId, issue.appId, version);

    const relatedIssues = useSelector(
        (state: RootState) => (issue.relatedIssueIds ?? []).map((id) => selectAppIssueById(state, issue.appId, id)),
        shallowEqual,
    );
    // Related issues may not be in the fetched list (other tab/filters), so cold-load the missing ones once.
    const fetchedRelatedIds = React.useRef(new Set<string>());
    React.useEffect(() => {
        (issue.relatedIssueIds ?? []).forEach((id, index) => {
            if (!relatedIssues[index] && !fetchedRelatedIds.current.has(id)) {
                fetchedRelatedIds.current.add(id);
                dispatch(tryFetchIssue({ orgId, appId: issue.appId, issueId: id }));
            }
        });
    }, [issue.relatedIssueIds, relatedIssues, dispatch, orgId, issue.appId]);
    const loadedRelatedIssues = relatedIssues.filter(Boolean) as IssueEntity[];

    // Deep-link from the card's related badge: scroll to the block once it has rendered (related
    // issues may still be cold-loading when the drawer opens).
    const location = useLocation();
    const shouldScrollToRelated = new URLSearchParams(location.search).get('scrollTo') === SCROLL_TO_RELATED_ISSUES;
    const relatedBlockRef = React.useRef<HTMLDivElement>(null);
    const hasScrolledToRelated = React.useRef(false);
    React.useEffect(() => {
        if (shouldScrollToRelated && !hasScrolledToRelated.current && relatedBlockRef.current) {
            hasScrolledToRelated.current = true;
            relatedBlockRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [shouldScrollToRelated, loadedRelatedIssues.length]);

    const statusTransitions = VALID_ISSUE_TRANSITIONS[issue.state].filter(
        (state) => requiresFixApprovals || !APPROVAL_DECISION_STATES.includes(state),
    );
    const statusOptions = [issue.state, ...statusTransitions]
        .sort((a, b) => ISSUE_STATE_ORDER.indexOf(a) - ISSUE_STATE_ORDER.indexOf(b))
        .map((state) => ({
            value: state,
            label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                        {stateMeta(state, requiresFixApprovals).icon}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {stateMeta(state, requiresFixApprovals).label}
                    </span>
                </span>
            ),
        }));

    const versionOptions = releases.map((release) => ({
        value: release.version,
        label: release.displayableVersion || release.version,
    }));

    const { agentLimitReason, refreshAgentIssueLimit } = useAgentIssueLimit(orgId, issue);

    const assigneeOptions = [
        ...sortUsersBySuggestedFirst(appUsers, issue.suggestedAssigneeUserIds).map((user) => {
            const name = displayUserNameLong(user);
            const isSuggested = !!issue.suggestedAssigneeUserIds?.includes(user.id);
            return {
                value: `${ASSIGNEE_USER_PREFIX}${user.id}`,
                name,
                avatarSrc: user.commsAvatars?.image32,
                label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, width: '100%' }}>
                        {optionAvatar(name, user.commsAvatars?.image32, isSuggested)}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {name}
                        </span>
                        {isSuggested && (
                            <img
                                src={sparkleIcon}
                                width={12}
                                height={12}
                                style={{ flexShrink: 0, marginLeft: 'auto' }}
                            />
                        )}
                    </span>
                ),
            };
        }),
        ...groups
            .filter((group) => !group.disabled)
            .map((group) => {
                const name = displayTextForUserChecklistGroup(group);
                return { value: `${ASSIGNEE_GROUP_PREFIX}${group.id}`, name, avatarSrc: undefined, label: name };
            }),
        ...assignableAgents.map((agent) => {
            const limitReason = agentLimitReason(agent.id);
            const label = (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar
                        size={20}
                        icon={<RobotOutlined />}
                        style={{ backgroundColor: antGrey.grey8, fontSize: 11 }}
                    />
                    {agent.name}
                </span>
            );
            return {
                value: `${ASSIGNEE_AGENT_PREFIX}${agent.id}`,
                name: agent.name,
                avatarSrc: undefined,
                disabled: !!limitReason,
                label:
                    limitReason ?
                        <Tooltip title={limitReason} placement="left">
                            {label}
                        </Tooltip>
                    :   label,
            };
        }),
    ];

    const selectedAssigneeKey = currentAssigneeKey(issue);
    const { assignIssue, modalContextHolder } = useAssignIssue(orgId, issue, viewType);
    const onAssigneeChange = (values: string[]) => assignIssue(values[values.length - 1]);

    const versionSelect = (
        <Tooltip title={noPermissionTooltip}>
            <Select
                value={version}
                options={versionOptions}
                showSearch
                optionFilterProp="label"
                allowClear
                disabled={isUpdatingIssue || !canManage}
                suffixIcon={
                    isUpdatingIssue ?
                        <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                    :   <DownOutlined style={{ color: antGrey.grey9, pointerEvents: 'none' }} />
                }
                placeholder="--"
                style={{ width: '100%' }}
                onChange={(value) =>
                    dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, version: value ?? '' }))
                }
            />
        </Tooltip>
    );

    const buildIdentifier = issue.build?.identifier || issue.build?.appStoreBuild;
    const buildOptions: DefaultOptionType[] = buildSelectOptions(
        builds.length > 0 ? builds
        : issue.build ? [issue.build]
        : [],
    );
    if (buildsLoading && buildOptions.length > 0) {
        buildOptions.push({ value: BUILDS_LOADING_KEY, searchLabel: '', label: 'Loading…', disabled: true });
    }
    const buildSelect = (
        <Tooltip title={version ? noPermissionTooltip : 'Select a version first'}>
            <Select
                value={buildIdentifier}
                options={buildOptions}
                showSearch
                optionFilterProp="searchLabel"
                allowClear
                loading={buildsLoading}
                disabled={isUpdatingIssue || !canManage || !version}
                suffixIcon={
                    isUpdatingIssue ?
                        <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                    :   <DownOutlined style={{ color: antGrey.grey9, pointerEvents: 'none' }} />
                }
                placeholder="--"
                notFoundContent={buildsLoading ? 'Loading…' : 'No builds found'}
                style={{ width: '100%' }}
                onChange={(value) =>
                    // Empty string explicitly clears the build server-side; a value pins it.
                    dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, build: value ?? '' }))
                }
            />
        </Tooltip>
    );

    return (
        <>
            {modalContextHolder}
            <Collapse
                className="triage-collapse"
                ghost
                bordered={false}
                defaultActiveKey={['section']}
                items={[
                    {
                        key: 'section',
                        showArrow: false,
                        style: { borderBottom: drawerSectionSeparator },
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <TagOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Issue</Text>
                            </div>
                        ),
                        extra: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {stateMeta(issue.state, requiresFixApprovals).icon}
                                <Text style={LABEL_STYLE}>{stateMeta(issue.state, requiresFixApprovals).label}</Text>
                            </div>
                        ),
                        children: (
                            <>
                                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Text
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 20,
                                                color: issue?.title ? antGrey.grey8 : antGrey.grey6,
                                                fontStyle: issue?.title ? undefined : 'italic',
                                            }}
                                        >
                                            {issue?.title || 'Add a title'}
                                        </Text>
                                        <PermissionedActionButton
                                            type="link"
                                            size="small"
                                            actionType={RunwayUserAction.UAManageTriageIssues}
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit('title')}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        {issue?.description ?
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <MarkdownParagraphWithEllipsis
                                                    content={issue.description}
                                                    maxHeight="160px"
                                                    backgroundColor="transparent"
                                                    markdownProps={{ components: DESCRIPTION_MARKDOWN_COMPONENTS }}
                                                    toggleAlign="right"
                                                    style={{
                                                        fontSize: 16,
                                                        fontFamily: 'inherit',
                                                        color:
                                                            issue.isDescriptionFromSource ?
                                                                antGrey.grey6
                                                            :   antGrey.grey8,
                                                    }}
                                                />
                                            </div>
                                        :   <Paragraph
                                                style={{
                                                    fontSize: 16,
                                                    color: antGrey.grey6,
                                                    fontStyle: 'italic',
                                                    flex: 1,
                                                    minWidth: 0,
                                                    marginBottom: 0,
                                                }}
                                            >
                                                No description
                                            </Paragraph>
                                        }
                                        <PermissionedActionButton
                                            type="link"
                                            size="small"
                                            actionType={RunwayUserAction.UAManageTriageIssues}
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit('description')}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12, padding: '0 16px 16px' }}>
                                    <FieldColumn label="Reported version">{versionSelect}</FieldColumn>
                                    <FieldColumn label="Build">{buildSelect}</FieldColumn>
                                </div>
                                <div style={{ display: 'flex', gap: 12, padding: '0 16px 16px' }}>
                                    <FieldColumn label="Status">
                                        <Tooltip title={noPermissionTooltip}>
                                            <Select
                                                value={issue.state}
                                                options={statusOptions}
                                                disabled={!canManage}
                                                style={{ width: '100%' }}
                                                suffixIcon={
                                                    <DownOutlined
                                                        style={{ color: antGrey.grey9, pointerEvents: 'none' }}
                                                    />
                                                }
                                                onChange={(value) => {
                                                    if (value === issue.state) {
                                                        return;
                                                    }
                                                    if (value === IssueState.FixRequested) {
                                                        setRequestFixOpen(true);
                                                        return;
                                                    }
                                                    dispatch(
                                                        tryTransitionIssue({
                                                            orgId,
                                                            appId: issue.appId,
                                                            issueId: issue.id,
                                                            targetState: value as IssueState,
                                                            viewType,
                                                        }),
                                                    );
                                                }}
                                            />
                                        </Tooltip>
                                    </FieldColumn>
                                    {reporterName && (
                                        <FieldColumn label="Reporter">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: 32,
                                                    padding: '3px 4px',
                                                    border: `1px solid ${antGrey.grey5}`,
                                                    borderRadius: 6,
                                                    background: runwayColors.white,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        maxWidth: '100%',
                                                        padding: '1px 8px',
                                                        borderRadius: 4,
                                                        background: antGrey.grey3,
                                                    }}
                                                >
                                                    {optionAvatar(reporterName, reporter?.commsAvatars?.image32)}
                                                    <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                                                        {reporterName}
                                                    </Text>
                                                </span>
                                            </div>
                                        </FieldColumn>
                                    )}
                                    <FieldColumn
                                        label={
                                            hasAutoAssignmentReasons(issue) ?
                                                <Tooltip
                                                    placement="top"
                                                    overlayStyle={{ maxWidth: 360 }}
                                                    title={<AutoAssignmentReasons issue={issue} basePath={basePath} />}
                                                >
                                                    <span
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                                    >
                                                        <span
                                                            style={{
                                                                textDecoration: 'underline dotted',
                                                                textUnderlineOffset: 3,
                                                            }}
                                                        >
                                                            Assignee
                                                        </span>
                                                        <img src={sparkleIcon} width={12} height={12} />
                                                    </span>
                                                </Tooltip>
                                            :   <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    Assignee
                                                    {issue.isAutoAssigned && (
                                                        <Tooltip title="Auto-assigned by Runway" placement="top">
                                                            <img src={sparkleIcon} width={12} height={12} />
                                                        </Tooltip>
                                                    )}
                                                </span>
                                        }
                                    >
                                        <Tooltip title={noPermissionTooltip}>
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                className="triage-assignee-select"
                                                disabled={isAssigning || !canManage}
                                                onDropdownVisibleChange={(open) => open && refreshAgentIssueLimit()}
                                                suffixIcon={
                                                    isAssigning ?
                                                        <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                                                    :   null
                                                }
                                                value={selectedAssigneeKey ? [selectedAssigneeKey] : []}
                                                options={assigneeOptions}
                                                filterOption={(input, option) =>
                                                    (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                tagRender={({ value, closable, onClose }) => {
                                                    const option = assigneeOptions.find((o) => o.value === value);
                                                    const isAgent = isAssigneeAgentKey(String(value));
                                                    const name =
                                                        isAgent ?
                                                            agentDisplayName(
                                                                String(value).slice(ASSIGNEE_AGENT_PREFIX.length),
                                                                agentsById[
                                                                    String(value).slice(ASSIGNEE_AGENT_PREFIX.length)
                                                                ],
                                                            )
                                                        :   (option?.name ?? String(value));
                                                    return (
                                                        <Tag
                                                            closable={closable}
                                                            onClose={onClose}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                height: 24,
                                                                margin: '2px 0',
                                                                ...(issue.isAutoAssigned ?
                                                                    {
                                                                        background: runwayColors.white,
                                                                        border: `1px dashed ${antGrey.grey5}`,
                                                                    }
                                                                :   { background: antGrey.grey3, border: 'none' }),
                                                            }}
                                                        >
                                                            {isAgent ?
                                                                <Avatar
                                                                    size={20}
                                                                    icon={<RobotOutlined />}
                                                                    style={{
                                                                        backgroundColor: antGrey.grey8,
                                                                        fontSize: 11,
                                                                    }}
                                                                />
                                                            :   optionAvatar(name, option?.avatarSrc)}
                                                            <span
                                                                style={{
                                                                    display: 'inline-block',
                                                                    maxWidth: 140,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    verticalAlign: 'middle',
                                                                }}
                                                            >
                                                                {name}
                                                            </span>
                                                        </Tag>
                                                    );
                                                }}
                                                placeholder="--"
                                                style={{ width: '100%' }}
                                                onChange={onAssigneeChange}
                                            />
                                        </Tooltip>
                                    </FieldColumn>
                                </div>
                                {(canRequestFix || canNotPriority) && (
                                    <div style={{ display: 'flex', gap: 12, padding: '0 16px 16px' }}>
                                        {canRequestFix && (
                                            <Tooltip title={noPermissionTooltip}>
                                                <Button
                                                    type="primary"
                                                    icon={<ToolOutlined />}
                                                    disabled={!canManage || isTransitioning}
                                                    onClick={() => setRequestFixOpen(true)}
                                                    style={{
                                                        flex: 1,
                                                        height: 40,
                                                        backgroundColor: canManage ? rwColors.statusYellow : undefined,
                                                        borderColor: canManage ? rwColors.statusYellow : undefined,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {requestFixActionLabel(requiresFixApprovals)}
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {canNotPriority && (
                                            <Tooltip title={noPermissionTooltip}>
                                                <Button
                                                    icon={<CloseCircleOutlined />}
                                                    loading={
                                                        isTransitioning && pendingTransition === IssueState.NotAPriority
                                                    }
                                                    disabled={!canManage || isTransitioning}
                                                    onClick={() => {
                                                        setPendingTransition(IssueState.NotAPriority);
                                                        dispatch(
                                                            tryTransitionIssue({
                                                                orgId,
                                                                appId: issue.appId,
                                                                issueId: issue.id,
                                                                targetState: IssueState.NotAPriority,
                                                                viewType,
                                                            }),
                                                        );
                                                    }}
                                                    style={{
                                                        flex: 1,
                                                        height: 40,
                                                        backgroundColor: canManage ? antGrey.grey7 : undefined,
                                                        borderColor: canManage ? antGrey.grey7 : undefined,
                                                        color: canManage ? runwayColors.white : undefined,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Not a priority
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                {loadedRelatedIssues.length > 0 && (
                                    <div
                                        ref={relatedBlockRef}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 12,
                                            margin: '0 16px 16px',
                                            padding: 16,
                                            border: `1px solid ${antGrey.grey5}`,
                                            borderRadius: 4,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                            }}
                                        >
                                            <LinkOutlined
                                                style={{ color: colorWithAlpha(rwColors.primary, 0.5), fontSize: 16 }}
                                            />
                                            <Text
                                                style={{
                                                    color: antGrey.grey8,
                                                    fontSize: 12,
                                                    letterSpacing: 1.2,
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                Related issues
                                            </Text>
                                        </div>
                                        {loadedRelatedIssues.map((related) => (
                                            <RelatedIssue
                                                key={related.id}
                                                issue={related}
                                                onOpen={() => history.push(`${basePath}/${related.id}`)}
                                                canManage={canManage}
                                                onUnlink={() =>
                                                    dispatch(
                                                        tryUnmarkIssueAsRelated({
                                                            orgId,
                                                            appId: issue.appId,
                                                            issueId: issue.id,
                                                            relatedIssueId: related.id,
                                                        }),
                                                    )
                                                }
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        ),
                    },
                ]}
            />
            <RequestFixModal
                open={requestFixOpen}
                onClose={() => setRequestFixOpen(false)}
                orgId={orgId}
                appId={issue.appId}
                issue={issue}
                viewType={viewType}
            />
            <Modal
                title={`Edit ${EDIT_FIELD_LABEL[editField]}`}
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                onOk={submitEdit}
                okText="Save"
                okButtonProps={{ disabled: editUnchanged || (editField === 'title' && !editValue.trim()) }}
                confirmLoading={isUpdatingIssue}
                destroyOnClose
                width={560}
            >
                {editField === 'title' ?
                    <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                :   <Input.TextArea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        autoFocus
                    />
                }
            </Modal>
        </>
    );
};
